import { Vector3 } from "three";
import { isDefined, QuadtreeUtils } from "../../infra3d";
import { MeasureHandler, STREAMTYPE } from "./internal";
/**
 * Calculates an z-coordinate form x,y-coordinate
 * @class Elevator
 * @constructor
 */
export class Elevator2 {
    // Pixelkoordinatensystem:
    // -------------------------->   u [pixel]
    //|
    //|
    //|        Vertikale Gerade als Funktion u(v)
    //|        u = m*v + q
    //|
    //|
    //|
    //V
    // v [pixel]
    // Bildkoordinatensystem
    //                 y' [mm]
    //                 |
    //                 |    Vertikale Gerad als Funktion x'(y')
    //                 |    x' = a*y' + b
    //                 |
    //                 |
    //                 |
    //                 |----------------->  x'[mm]
    // Kamerakoordinatensystem (3D-Koordinatensystem)
    // Ursprung im Projektionszentrum der Kamera
    // x in m nach rechts
    // y in m nach oben
    // z in m nach hinten
    // Weltkoordinatensystem X,Y,Z
    constructor(geoframe, sensorid, cbf, cubefaceindex) {
        this.cbf = cbf;
        this._sensorid = sensorid;
        if (cubefaceindex == null || !isDefined(cubefaceindex))
            cubefaceindex = sensorid;
        this._cubefaceindex = cubefaceindex;
        const mi = geoframe.getMeasureInfo();
        this._tilepath = geoframe.dataPath + "/" + this._cubefaceindex;
        this.curlod = mi.depthmapmeta.maxzoom;
        this._quadtreesize = mi.depthmapmeta.tilesize * Math.pow(2, mi.depthmapmeta.maxzoom);
        this._u = 0;
        this._v = 0;
        this._b_disp = 0;
        this._a_disp = 0;
        this._R = null;
        this._C_global = null;
        this._m = 0;
        this._q = 0;
        this.geoframe = geoframe;
        this._R = MeasureHandler.getRotationMatrix((geoframe.omega * Math.PI) / 180, (geoframe.phi * Math.PI) / 180, (geoframe.kappa * Math.PI) / 180);
        const sensorpos = mi.sensors[this._sensorid].position;
        let R_c_c0 = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        if (mi.streamtype == STREAMTYPE.CUBEMAP) {
            const cubeorientations = (geoframe.parentGeoFrameCollection).getCubeOrientations();
            const cubeorientation = cubeorientations[this._cubefaceindex];
            R_c_c0 = MeasureHandler.getRotationMatrix((cubeorientation.domega * Math.PI) / 180, (cubeorientation.dphi * Math.PI) / 180, (cubeorientation.dkappa * Math.PI) / 180);
        }
        const r_world_frame = new Vector3(geoframe.easting, geoframe.northing, geoframe.height);
        this._C_global = new Vector3(sensorpos.dx, sensorpos.dy, sensorpos.dz);
        this._C_global.applyMatrix4(this._R);
        this._C_global.add(r_world_frame);
        this._R.multiply(R_c_c0);
        this._R.transpose();
    }
    calc(X, Y, pointid) {
        this._pointid = pointid;
        // Vorbereitungen
        const mi = this.geoframe.getMeasureInfo();
        const dX = X - this._C_global.x;
        const dY = Y - this._C_global.y;
        const r12 = this._R.elements[0 * 4 + 1];
        const r22 = this._R.elements[1 * 4 + 1];
        const r11 = this._R.elements[0 * 4 + 0];
        const r21 = this._R.elements[1 * 4 + 0];
        const r13 = this._R.elements[0 * 4 + 2];
        const r23 = this._R.elements[1 * 4 + 2];
        const r33 = this._R.elements[2 * 4 + 2];
        const r32 = this._R.elements[2 * 4 + 1];
        //Lineare Funktion der vertikalen Gerade im Bildkoordinatensystem
        //x' = a*y' + b
        const a = (-dY * r12 + dX * r22) / (dY * r11 - dX * r21);
        const b = mi.depthmapmeta.focallength * ((dY * r13 - dX * r23) / (dY * r11 - dX * r21));
        //Lineare Funktion der vertikalen Gerade im Pixelkoordinatensystem
        //u = m*v + q
        this._m = -1 * a;
        this._q =
            b / (mi.depthmapmeta.pixsize * 0.001) - (this._m * mi.depthmapmeta.height) / 2 + mi.depthmapmeta.width / 2;
        //Lineare Funktion zur Berechnung der Solltiefe
        //z = a_disp * y + b_disp
        this._a_disp = r33 / r32;
        this._b_disp = dX * r13 + dY * r23 - (dX * r12 + dY * r22) * (r33 / r32);
        //Berechnung der Startwerte fuer u und v
        this._v = mi.depthmapmeta.height - 1;
        this._u = this._m * this._v + this._q;
        if (this._u < mi.depthmapmeta.width && this._u >= 0 && this._v >= 0 && this._v < mi.depthmapmeta.height) {
            //Berechnung Quadcode und Laden des entsprechenden Tiles
            const quadcode = QuadtreeUtils.NormalizedCoordToQuadKey(this._u / this._quadtreesize, this._v / this._quadtreesize, this.curlod);
            let quadtree = null;
            if (mi.streamtype == STREAMTYPE.CUBEMAP) {
                quadtree = (this.geoframe.parentGeoFrameCollection.imagequadtree);
            }
            else if ([STREAMTYPE.STEREO, STREAMTYPE.MONO].indexOf(mi.streamtype) >= 0) {
                quadtree = this.geoframe.parentGeoFrameCollection.disparityquadtree;
            }
            else {
                quadtree = this.geoframe.parentGeoFrameCollection.disparityquadtree;
            }
            quadtree.getDisparityTile(quadcode, this._cubefaceindex, (tile) => {
                this._cbfTileIsLoaded(tile);
            }, () => {
                this.cbf(null, 0);
            });
        }
        else {
            this.cbf(null, 0);
            return;
        }
    }
    _cbfTileIsLoaded(tile) {
        const mi = this.geoframe.getMeasureInfo();
        const diff_soll_ist_prev = 500;
        let disp_soll_prev = 0;
        let disp_ist_prev = 0;
        while (this._u / this._quadtreesize >= tile.extent[0] &&
            this._u / this._quadtreesize < tile.extent[2] &&
            this._v / this._quadtreesize >= tile.extent[1] &&
            this._v / this._quadtreesize < tile.extent[3]) {
            //Abfragen Disparitaetswert
            const disp = tile.getDisparityValue(this._u / this._quadtreesize, this._v / this._quadtreesize);
            const disp_ist = disp[0];
            if (disp_ist > 100) {
                //Umrechnung von v in y' (Pixelkoordinaten --> Bildkoordinaten)
                const y_img = mi.depthmapmeta.pixsize * 0.001 * (mi.depthmapmeta.height / 2 - this._v);
                //Umbrechnung von y' in y (Bildkoordinaten --> Kamerakoordinaten)
                const Y_p = (this._b_disp * y_img) / (-1 * mi.depthmapmeta.focallength - this._a_disp * y_img);
                //Berechnung der Solltiefe (z im Kamerakoordinatensystem)
                const z_soll = this._a_disp * Y_p + this._b_disp;
                //Umrechnung der Solltiefe in eine Solldisparitaet
                const disp_soll = (mi.depthmapmeta.focallength * mi.depthmapmeta.basis * 65536) /
                    (-1 * z_soll * mi.depthmapmeta.numdisparities * mi.depthmapmeta.pixsize * 0.001);
                //Vergleich der Solldisparitaet mit der Ist-Disparitaet und Entscheidung
                const diff_soll_ist = Math.abs(disp_ist - disp_soll);
                if (diff_soll_ist < 500) {
                    if (Elevator2.checkcrossing(disp_soll_prev, disp_ist_prev, disp_soll, disp_ist)) {
                        const x_img = (this._u - mi.depthmapmeta.width / 2) * mi.depthmapmeta.pixsize * 0.001;
                        const X_p = (z_soll / (-1 * mi.depthmapmeta.focallength)) * x_img;
                        const P_cam = new Vector3(X_p, Y_p, z_soll);
                        const P_global = P_cam.applyMatrix4(this._R.transpose());
                        P_global.add(this._C_global);
                        this.cbf(P_global.z, disp[1]);
                        return;
                    }
                    else {
                        disp_soll_prev = disp_soll;
                        disp_ist_prev = disp_ist;
                    }
                }
            }
            this._v = this._v - 1;
            this._u = this._m * this._v + this._q;
        }
        if (isNaN(this._u) || isNaN(this._v) || this._v < 0 || this._u < 0) {
            this.cbf(null, 0);
            return;
        }
        this._v = this._v - 1;
        this._u = this._m * this._v + this._q;
        if (this._u < mi.depthmapmeta.width && this._u >= 0 && this._v >= 0 && this._v < mi.depthmapmeta.height) {
            const quadcode = QuadtreeUtils.NormalizedCoordToQuadKey(this._u / this._quadtreesize, this._v / this._quadtreesize, this.curlod);
            let quadtree = null;
            if (mi.streamtype == STREAMTYPE.CUBEMAP) {
                quadtree = (this.geoframe.parentGeoFrameCollection.imagequadtree);
            }
            else if ([STREAMTYPE.STEREO, STREAMTYPE.MONO].indexOf(mi.streamtype) >= 0) {
                quadtree = this.geoframe.parentGeoFrameCollection.disparityquadtree;
            }
            else {
                quadtree = this.geoframe.parentGeoFrameCollection.disparityquadtree;
            }
            quadtree.getDisparityTile(quadcode, this._cubefaceindex, (tile) => {
                this._cbfTileIsLoaded(tile);
            }, () => {
                this.cbf(null, 0);
            });
        }
        else {
            this.cbf(null, 0);
            return;
        }
    }
    /**
     * Checks if nominal disparity crosses the actual disparity
     * @param {number} ds1 disparitaet soll pixel x - 1
     * @param {number} di1 disparitaet ist pixel x - 1
     * @param {number} ds2 disparitaet soll pixel x
     * @param {number} di2 disparitaet ist pixel x
     * @returns {boolean}
     */
    static checkcrossing(ds1, di1, ds2, di2) {
        if (ds1 == 0 && di1 == 0)
            return false;
        const negative1 = ds1 - di1 < 0;
        const negative2 = ds2 - di2 < 0;
        return !(negative1 == negative2);
    }
}
//# sourceMappingURL=elevator2.js.map